import React, { useEffect, useState, Children, memo, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, Star, Shield, AlertTriangle, Sparkles, PawPrint, X, MessageCircle } from 'lucide-react';
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
const pulseGlow = {
  initial: {
    boxShadow: '0 0 0 rgba(74, 222, 128, 0)'
  },
  animate: {
    boxShadow: ['0 0 0 0px rgba(74, 222, 128, 0.2)', '0 0 0 10px rgba(74, 222, 128, 0)', '0 0 0 0px rgba(74, 222, 128, 0)'],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }
};
export function SalesPage() {
  const [activeModal, setActiveModal] = useState<null | 'privacy' | 'terms'>(null);
  const closeModal = () => setActiveModal(null);
  // ── Meta Pixel: init once + PageView ──────────────────────────────────────
  useEffect(() => {
    // Guard: skip if already initialized
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
      return;
    }
    // Build the fbq stub manually (safe — no getElementsByTagName)
    const n: any = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };
    window._fbq = n;
    window.fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    // Inject fbevents.js via document.head.appendChild (avoids parentNode crash)
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    window.fbq('init', '775973745574636');
    window.fbq('track', 'PageView');
  }, []);
  // ── Buy button handler ────────────────────────────────────────────────────
  const handleBuyClick = () => {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'InitiateCheckout');
    }
    window.location.href = 'https://www.mellowreset.com/cart/43015681900610:1';
  };
  return <div className="min-h-screen bg-[#0a0f0a] text-[#f0fdf4] overflow-x-hidden selection:bg-[#22c55e] selection:text-black" data-id="element-2">
      {/* --- HERO SECTION --- */}
      <section className="relative pt-12 pb-16 px-4 overflow-hidden" data-id="element-3">
        {/* Background Effects */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[800px] bg-gradient-to-b from-[#1a2e1a] to-transparent opacity-40 pointer-events-none" data-id="element-4" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#22c55e] rounded-full blur-[150px] opacity-10 pointer-events-none" data-id="element-5" />

        <div className="max-w-6xl mx-auto relative z-10 flex flex-col items-center text-center" data-id="element-6">
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#111a11] border border-[#22c55e]/30 text-[#4ade80] text-sm font-medium mb-4 shadow-[0_0_15px_rgba(34,197,94,0.2)]" data-id="element-7">
            <PawPrint size={16} data-id="element-8" />
            <span className="text-[16px] text-[#0BF2A5]" data-id="element-9">
              Para Adolescentes y Jovenes Therian en Crisis de Identidad
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-4 drop-shadow-[0_0_25px_rgba(34,197,94,0.3)]" data-id="element-10">
            <span className="block text-white" data-id="element-11">¿Soy "real"</span>
            <span className="block text-[#4ade80] mt-2" data-id="element-12">
              o solo estoy fingiendo?
            </span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.4
        }} className="text-xl md:text-2xl text-gray-300 max-w-3xl mb-8 font-light" data-id="element-13">
            Descúbrelo hoy. Deja de buscar en tests de internet; encuentra tu
            verdad en tu instinto.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="relative w-full max-w-4xl mx-auto mb-8 group" data-id="element-14">
            <div className="absolute inset-0 bg-[#22c55e] blur-[60px] opacity-20 group-hover:opacity-30 transition-opacity duration-700 rounded-full" data-id="element-15" />
            <img src="https://cdn.magicpatterns.com/uploads/2oza45mBBmNpaQZG8WGeAA/SOS_Despertar_Identidad_Therian.png" alt="SOS Despertar Guía Digital" className="relative z-10 w-full h-auto drop-shadow-2xl transform hover:scale-[1.02] transition-transform duration-500" data-id="element-16" />
          </motion.div>

          <motion.div variants={pulseGlow} initial="initial" animate="animate" data-id="element-17">
            <button onClick={() => document.getElementById('oferta')?.scrollIntoView({
            behavior: 'smooth'
          })} className="bg-[#22c55e] hover:bg-[#16a34a] text-black font-bold text-lg md:text-xl px-8 py-4 rounded-lg transition-all duration-300 transform hover:-translate-y-1 flex items-center gap-3 shadow-[0_0_30px_rgba(34,197,94,0.4)]" data-id="element-18">
              <span data-id="element-19">QUIERO MI CERTEZA AHORA</span>
              <ArrowRight size={24} data-id="element-20" />
            </button>
          </motion.div>
          <p className="mt-4 text-gray-500 flex items-center gap-2 text-[18px]" data-id="element-21">
            <Shield size={14} className="text-[#22c55e]" data-id="element-22" />
            Descarga inmediata y segura
          </p>
        </div>
      </section>

      {/* --- THE PAIN (EL LEAD) --- */}
      <section className="py-12 px-4 bg-[#0d1a0d] relative" data-id="element-23">
        <div className="max-w-3xl mx-auto" data-id="element-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="prose prose-invert prose-lg max-w-none" data-id="element-25">
            <h2 className="text-3xl md:text-4xl text-[#f59e0b] mb-6 text-center font-bold" data-id="element-26">
              La agonía silenciosa de la duda
            </h2>

            <p className="text-gray-300 leading-relaxed mb-4" data-id="element-27">
              Estás en tu habitación, miras tu reflejo en el espejo y algo no
              encaja. Sientes ese impulso en la base de la columna, ese deseo de
              correr por el bosque, esa sensación de que tus manos deberían ser
              garras.
            </p>

            <p className="text-gray-300 leading-relaxed mb-4" data-id="element-28">
              Pero luego entras a TikTok, ves a miles de personas con máscaras
              perfectas y saltos increíbles, y una voz en tu cabeza te susurra:
            </p>

            <div className="bg-[#111a11] border-l-4 border-[#f59e0b] p-6 my-6 rounded-r-lg" data-id="element-29">
              <p className="text-xl text-white italic font-medium m-0" data-id="element-30">
                "¿Y si solo me lo estoy inventando? ¿Y si solo quiero encajar?"
              </p>
            </div>

            <p className="text-gray-300 leading-relaxed" data-id="element-31">
              Esa duda es el{' '}
              <strong className="text-[#4ade80]" data-id="element-32">
                Síndrome del Impostor Therian
              </strong>
              , y es la agonía más silenciosa de nuestra comunidad. Te hace
              sentir que no perteneces a ningún lado: ni al mundo humano, ni al
              mundo animal.
            </p>
          </motion.div>
        </div>
      </section>

      {/* --- WHY OTHERS FAILED --- */}
      <section className="py-12 px-4 bg-[#0a0f0a] relative overflow-hidden" data-id="element-33">
        {/* Decorative elements */}
        <div className="absolute right-0 top-1/3 w-64 h-64 bg-[#f59e0b] rounded-full blur-[120px] opacity-5 pointer-events-none" data-id="element-34" />

        <div className="max-w-5xl mx-auto" data-id="element-35">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-10" data-id="element-36">
            <h2 className="text-3xl md:text-5xl text-white mb-6" data-id="element-37">
              Por qué has fallado hasta ahora
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-38">
              El problema no eres tú. El problema es dónde has estado buscando.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-39">
            {[{
            icon: <AlertTriangle className="text-[#f59e0b] w-10 h-10" data-id="element-40" />,
            title: 'Tests de Internet',
            desc: 'Has hecho tests gratuitos en Google que parecen juegos de niños y no profundizan en tu verdadera psique.'
          }, {
            icon: <MessageCircle className="text-[#f59e0b] w-10 h-10" data-id="element-41" />,
            title: 'Foros Confusos',
            desc: 'Has preguntado en redes solo para recibir respuestas contradictorias que aumentan tu ansiedad.'
          }, {
            icon: <Sparkles className="text-[#f59e0b] w-10 h-10" data-id="element-42" />,
            title: 'Shifts Forzados',
            desc: "Has intentado 'forzar' una conexión animal solo para sentirte más falso y vacío que antes."
          }].map((item, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2
          }} className="bg-[#111a11] p-8 rounded-xl border border-[#ffffff]/5 hover:border-[#f59e0b]/30 transition-colors duration-300" data-id="element-43">
                <div className="mb-6 bg-[#1a2e1a] w-16 h-16 rounded-full flex items-center justify-center" data-id="element-44">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-45">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-46">{item.desc}</p>
              </motion.div>)}
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="mt-10 text-center max-w-3xl mx-auto" data-id="element-47">
            <p className="text-lg md:text-xl text-white font-medium" data-id="element-48">
              Te han enseñado a buscar tu identidad en la pantalla, cuando la
              respuesta está grabada en tu{' '}
              <span className="text-[#4ade80] underline decoration-[#4ade80]/50 underline-offset-4" data-id="element-49">
                sistema nervioso
              </span>
              .
            </p>
          </motion.div>
        </div>
      </section>

      {/* --- THE SOLUTION (MECHANISM) --- */}
      <section className="py-12 px-4 bg-gradient-to-b from-[#0d1a0d] to-[#0a0f0a] relative" data-id="element-50">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-10" data-id="element-51">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="w-full md:w-1/2" data-id="element-52">
            <div className="relative" data-id="element-53">
              <div className="absolute inset-0 bg-[#4ade80] blur-[80px] opacity-10 rounded-full" data-id="element-54" />
              <img src="https://cdn.magicpatterns.com/uploads/2oza45mBBmNpaQZG8WGeAA/SOS_Despertar_Identidad_Therian.png" alt="Matriz de Validación Sensorial" className="relative z-10 w-full rounded-lg shadow-2xl" data-id="element-55" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="w-full md:w-1/2" data-id="element-56">
            <div className="inline-block px-3 py-1 bg-[#22c55e]/10 text-[#4ade80] rounded-full font-bold mb-4 border border-[#22c55e]/20 text-[18px]" data-id="element-57">
              EL MECANISMO ÚNICO
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-58">
              Matriz de Validación Sensorial (MVS)
            </h2>
            <p className="text-gray-300 text-lg mb-6 leading-relaxed" data-id="element-59">
              Un sistema diseñado para separar el "ruido digital" del "instinto
              biológico". No es un juego. Es un proceso científico y espiritual
              de 3 capas:
            </p>

            <ul className="space-y-4" data-id="element-60">
              {[{
              title: 'Capa 1: Validación Pasiva',
              desc: 'Identifica señales subconscientes que no puedes fingir.'
            }, {
              title: 'Capa 2: Validación Sensorial',
              desc: 'Conecta con los estímulos físicos de tu Theriotipo.'
            }, {
              title: 'Capa 3: Validación de Realidad',
              desc: 'Distingue entre fantasía creativa y memoria animal.'
            }].map((layer, idx) => <li key={idx} className="flex items-start gap-4" data-id="element-61">
                  <div className="mt-1 bg-[#22c55e] rounded-full p-1 shrink-0" data-id="element-62">
                    <Check size={14} className="text-black" data-id="element-63" />
                  </div>
                  <div data-id="element-64">
                    <h4 className="text-white font-bold text-lg" data-id="element-65">
                      {layer.title}
                    </h4>
                    <p className="text-gray-400" data-id="element-66">{layer.desc}</p>
                  </div>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* --- THE OFFER --- */}
      <section className="py-12 px-4 bg-[#0a0f0a] relative" data-id="element-67">
        <div id="oferta" className="max-w-4xl mx-auto" data-id="element-68">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-10" data-id="element-69">
            <h2 className="text-3xl md:text-5xl text-white mb-4" data-id="element-70">
              SOS Despertar:{' '}
              <span className="text-[#4ade80]" data-id="element-71">¿Soy Therian Realmente?</span>
            </h2>
            <p className="text-xl text-gray-400" data-id="element-72">
              La guía definitiva para encontrar tu certeza.
            </p>
          </motion.div>

          <div className="bg-[#111a11] rounded-2xl border border-[#22c55e]/20 p-8 md:p-10 relative overflow-hidden shadow-[0_0_50px_rgba(34,197,94,0.1)]" data-id="element-73">
            {/* Glow effect inside card */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#22c55e] blur-[100px] opacity-10 pointer-events-none" data-id="element-74" />

            <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-75">
              <div className="space-y-6" data-id="element-76">
                <h3 className="font-bold text-white mb-4 text-[28px]" data-id="element-77">
                  Lo que obtendrás hoy:
                </h3>
                <motion.ul variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
                once: true
              }} className="space-y-3" data-id="element-78">
                  {['El método exacto para identificar tu Theriotipo sin margen de error.', 'Técnicas de anclaje para controlar tus shifts en la escuela.', 'La guía para manejar la disforia de especie.', "Estrategias para superar el miedo al 'qué dirán'.", 'Acceso a la Matriz de Validación Sensorial.'].map((item, idx) => <motion.li key={idx} variants={fadeInUp} className="flex items-start gap-3 text-gray-300" data-id="element-79">
                      <Star className="text-[#f59e0b] shrink-0 mt-1" size={18} fill="#f59e0b" data-id="element-80" />
                      <span data-id="element-81">{item}</span>
                    </motion.li>)}
                </motion.ul>

                <div className="bg-[#1a2e1a] p-4 rounded-lg border border-[#22c55e]/30 mt-6 space-y-3" data-id="element-82">
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-1" data-id="element-83">
                    <span className="bg-[#f59e0b] text-black text-xs font-bold px-2 py-1 rounded" data-id="element-84">
                      BONOS EXCLUSIVOS
                    </span>
                    <span className="text-gray-400 text-xs line-through" data-id="element-85">
                      $60.00
                    </span>
                    <span className="text-[#4ade80] text-xs font-bold" data-id="element-86">
                      GRATIS
                    </span>
                  </div>

                  {/* Bono 1 */}
                  <div className="flex items-center gap-3" data-id="element-87">
                    <img src="https://cdn.magicpatterns.com/uploads/a9R686q2Vh3zio9K5yrRrV/bonoss_1.png" alt="Therian Confirmado" className="w-12 h-12 rounded object-cover shrink-0" data-id="element-88" />
                    <div className="min-w-0" data-id="element-89">
                      <p className="text-white text-xs font-bold leading-tight" data-id="element-90">
                        BONO 1: Therian Confirmado
                      </p>
                      <p className="text-gray-400 text-[11px] leading-tight mt-0.5" data-id="element-91">
                        Checklist de 7 días para detectar tu Theriotipo sin
                        tests tontos.
                      </p>
                      <span className="text-gray-500 text-[10px] line-through" data-id="element-92">
                        $30
                      </span>
                      <span className="text-[#4ade80] text-[10px] font-bold ml-1" data-id="element-93">
                        GRATIS
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-[#22c55e]/10" data-id="element-94" />

                  {/* Bono 2 */}
                  <div className="flex items-center gap-3" data-id="element-95">
                    <img src="https://cdn.magicpatterns.com/uploads/m8Kuh8kVB7RQcmWVYvo5hD/bonoss_2.png" alt="Protocolo Anti-Pánico" className="w-12 h-12 rounded object-cover shrink-0" data-id="element-96" />
                    <div className="min-w-0" data-id="element-97">
                      <p className="text-white text-xs font-bold leading-tight" data-id="element-98">
                        BONO 2: Protocolo Anti-Pánico
                      </p>
                      <p className="text-gray-400 text-[11px] leading-tight mt-0.5" data-id="element-99">
                        Qué hacer en un shift (en público o en casa) en 3
                        minutos.
                      </p>
                      <span className="text-gray-500 text-[10px] line-through" data-id="element-100">
                        $30
                      </span>
                      <span className="text-[#4ade80] text-[10px] font-bold ml-1" data-id="element-101">
                        GRATIS
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center text-center" data-id="element-102">
                <div className="flex items-center gap-3 mb-1" data-id="element-103">
                  <span className="text-2xl text-gray-500 line-through" data-id="element-104">
                    $25.99
                  </span>
                  <span className="bg-[#22c55e] text-black text-xs font-bold px-2 py-1 rounded-full" data-id="element-105">
                    -71.2% OFF
                  </span>
                </div>
                <div className="text-5xl font-bold text-white mb-2" data-id="element-106">$7.49</div>
                <p className="text-[#4ade80] text-sm mb-6" data-id="element-107">
                  Oferta especial por Tiempo Limitado
                </p>

                <motion.div variants={pulseGlow} initial="initial" animate="animate" className="w-full" data-id="element-108">
                  <button onClick={handleBuyClick} className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-black font-bold text-lg py-4 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg flex justify-center items-center gap-2" data-id="element-109">
                    <span data-id="element-110">DESCARGAR AHORA "SOS Despertar"</span>
                    <ArrowRight size={20} data-id="element-111" />
                  </button>
                </motion.div>

                <p className="mt-4 text-gray-500 text-[14px]" data-id="element-112">
                  Acceso digital inmediato. Pago 100% seguro.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- FINAL CTA --- */}
      <section className="py-12 px-4 bg-[#0d1a0d] text-center relative overflow-hidden" data-id="element-113">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" data-id="element-114" />

        <div className="max-w-3xl mx-auto relative z-10" data-id="element-115">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-116">
            No pases una noche más preguntándote si eres "falso"
          </motion.h2>

          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-gray-300 mb-8" data-id="element-117">
            La duda drena tu energía. La certeza te da poder. Haz clic abajo,
            descarga tu guía y abraza tu verdadera naturaleza hoy mismo.
          </motion.p>

          <motion.div variants={pulseGlow} initial="initial" animate="animate" className="inline-block" data-id="element-118">
            <button onClick={handleBuyClick} className="bg-[#f59e0b] hover:bg-[#d97706] text-black font-bold text-xl px-10 py-5 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-[0_0_30px_rgba(245,158,11,0.4)] flex items-center gap-3" data-id="element-119">
              <span data-id="element-120">OBTENER MI GUÍA SOS</span>
              <ArrowRight size={24} data-id="element-121" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="py-12 bg-[#050805] text-center text-gray-500 text-sm border-t border-[#111a11]" data-id="element-122">
        <div className="max-w-6xl mx-auto px-4" data-id="element-123">
          <div className="flex items-center justify-center gap-2 mb-4" data-id="element-124">
            <PawPrint size={14} className="text-[#22c55e]" data-id="element-125" />
            <span className="text-gray-600" data-id="element-126">SOS Despertar</span>
          </div>
          <p className="mb-4 text-gray-700" data-id="element-127">
            © 2025 SOS Despertar. Todos los derechos reservados.
          </p>
          <div className="flex justify-center gap-6" data-id="element-128">
            <button onClick={() => setActiveModal('terms')} className="hover:text-[#4ade80] transition-colors duration-200 underline underline-offset-4 decoration-gray-700 hover:decoration-[#4ade80]" data-id="element-129">
              Términos y Condiciones
            </button>
            <button onClick={() => setActiveModal('privacy')} className="hover:text-[#4ade80] transition-colors duration-200 underline underline-offset-4 decoration-gray-700 hover:decoration-[#4ade80]" data-id="element-130">
              Política de Privacidad
            </button>
          </div>
        </div>
      </footer>

      {/* --- MODALS --- */}
      <AnimatePresence data-id="element-131">
        {activeModal && <motion.div key="backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={closeModal} data-id="element-132">
            <motion.div key="modal" initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="relative bg-[#0d1a0d] border border-[#22c55e]/20 rounded-2xl shadow-[0_0_60px_rgba(34,197,94,0.15)] w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-133">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-8 py-5 border-b border-[#22c55e]/10 shrink-0" data-id="element-134">
                <h2 className="text-lg font-bold text-white tracking-wide" data-id="element-135">
                  {activeModal === 'terms' ? 'Términos y Condiciones' : 'Política de Privacidad'}
                </h2>
                <button onClick={closeModal} className="text-gray-500 hover:text-[#4ade80] transition-colors duration-200 p-1 rounded-full hover:bg-[#22c55e]/10" data-id="element-136">
                  <X size={20} data-id="element-137" />
                </button>
              </div>

              {/* Modal Body — scrollable */}
              <div className="overflow-y-auto px-8 py-6 text-gray-300 text-sm leading-relaxed space-y-5 scrollbar-thin scrollbar-thumb-[#22c55e]/20 scrollbar-track-transparent" data-id="element-138">
                {activeModal === 'terms' ? <>
                    <p className="text-[#4ade80] font-semibold text-xs uppercase tracking-widest" data-id="element-139">
                      Última actualización: Enero 2025
                    </p>
                    <p data-id="element-140">
                      Al acceder y utilizar el producto digital{' '}
                      <strong className="text-white" data-id="element-141">
                        SOS Despertar: ¿Soy Therian Realmente?
                      </strong>
                      , aceptas los siguientes términos y condiciones en su
                      totalidad.
                    </p>

                    <h3 className="text-white font-bold text-base mt-4" data-id="element-142">
                      1. Uso del Producto
                    </h3>
                    <p data-id="element-143">
                      Este producto es de uso personal y no comercial. Está
                      diseñado exclusivamente como material informativo y de
                      autoexploración. No constituye diagnóstico médico,
                      psicológico ni terapéutico de ningún tipo.
                    </p>

                    <h3 className="text-white font-bold text-base mt-4" data-id="element-144">
                      2. Propiedad Intelectual
                    </h3>
                    <p data-id="element-145">
                      Todo el contenido incluido en esta guía —textos,
                      metodologías, gráficos y la Matriz de Validación Sensorial
                      (MVS)— es propiedad exclusiva de SOS Despertar. Queda
                      prohibida su reproducción, distribución o reventa sin
                      autorización escrita.
                    </p>

                    <h3 className="text-white font-bold text-base mt-4" data-id="element-146">
                      3. Política de Reembolso
                    </h3>
                    <p data-id="element-147">
                      Dado que se trata de un producto digital de descarga
                      inmediata, no se ofrecen reembolsos una vez completada la
                      compra y entregado el acceso. Si experimentas problemas
                      técnicos, contáctanos dentro de las 48 horas siguientes a
                      tu compra.
                    </p>

                    <h3 className="text-white font-bold text-base mt-4" data-id="element-148">
                      4. Limitación de Responsabilidad
                    </h3>
                    <p data-id="element-149">
                      SOS Despertar no se hace responsable de decisiones
                      personales tomadas a partir del contenido de esta guía. Si
                      experimentas malestar emocional significativo, te
                      recomendamos buscar apoyo profesional.
                    </p>

                    <h3 className="text-white font-bold text-base mt-4" data-id="element-150">
                      5. Modificaciones
                    </h3>
                    <p data-id="element-151">
                      Nos reservamos el derecho de actualizar estos términos en
                      cualquier momento. El uso continuado del producto implica
                      la aceptación de los términos vigentes.
                    </p>
                  </> : <>
                    <p className="text-[#4ade80] font-semibold text-xs uppercase tracking-widest" data-id="element-152">
                      Última actualización: Enero 2025
                    </p>
                    <p data-id="element-153">
                      En <strong className="text-white" data-id="element-154">SOS Despertar</strong>{' '}
                      nos comprometemos a proteger tu privacidad. Esta política
                      describe cómo recopilamos, usamos y protegemos tu
                      información.
                    </p>

                    <h3 className="text-white font-bold text-base mt-4" data-id="element-155">
                      1. Información que Recopilamos
                    </h3>
                    <p data-id="element-156">
                      Al realizar una compra, recopilamos únicamente los datos
                      necesarios para procesar el pago y entregar el producto:
                      nombre, correo electrónico y datos de facturación. No
                      almacenamos información de tarjetas de crédito.
                    </p>

                    <h3 className="text-white font-bold text-base mt-4" data-id="element-157">
                      2. Uso de la Información
                    </h3>
                    <p data-id="element-158">
                      Tu información se utiliza exclusivamente para: (a)
                      procesar tu compra, (b) enviarte el acceso al producto, y
                      (c) comunicaciones relacionadas con tu pedido. No vendemos
                      ni compartimos tus datos con terceros.
                    </p>

                    <h3 className="text-white font-bold text-base mt-4" data-id="element-159">
                      3. Cookies
                    </h3>
                    <p data-id="element-160">
                      Este sitio puede utilizar cookies técnicas esenciales para
                      su funcionamiento. No utilizamos cookies de rastreo
                      publicitario de terceros sin tu consentimiento.
                    </p>

                    <h3 className="text-white font-bold text-base mt-4" data-id="element-161">
                      4. Seguridad
                    </h3>
                    <p data-id="element-162">
                      Implementamos medidas de seguridad estándar de la
                      industria para proteger tu información. Las transacciones
                      se procesan a través de plataformas de pago certificadas
                      con cifrado SSL.
                    </p>

                    <h3 className="text-white font-bold text-base mt-4" data-id="element-163">
                      5. Tus Derechos
                    </h3>
                    <p data-id="element-164">
                      Tienes derecho a solicitar el acceso, corrección o
                      eliminación de tus datos personales en cualquier momento.
                      Para ejercer estos derechos, contáctanos por correo
                      electrónico.
                    </p>

                    <h3 className="text-white font-bold text-base mt-4" data-id="element-165">
                      6. Menores de Edad
                    </h3>
                    <p data-id="element-166">
                      Este producto está dirigido a adolescentes. Si eres menor
                      de 13 años, necesitas el consentimiento de un padre o
                      tutor para realizar una compra.
                    </p>
                  </>}
              </div>

              {/* Modal Footer */}
              <div className="px-8 py-4 border-t border-[#22c55e]/10 shrink-0" data-id="element-167">
                <button onClick={closeModal} className="w-full bg-[#111a11] hover:bg-[#1a2e1a] border border-[#22c55e]/20 hover:border-[#22c55e]/50 text-[#4ade80] font-medium py-3 rounded-lg transition-all duration-200 text-sm" data-id="element-168">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}